import Decimal from 'decimal.js';

import type { EventVo } from 'models/event';
import type { FeeType, SeriesVo } from 'models/series';

const TAKER_FEE = new Decimal(0.07);
const MAKER_FEE = new Decimal(0.0175);

/**
 * TODO(2026-08-20): Remove this temporary web-only MLB pre-game override.
 * Rollback by deleting these MLB constants and `resolveSeriesFeeMultiplier`, then
 * restoring `resolveEffectiveFeeParams` to fall back directly to
 * `series?.fee_multiplier`. Also remove the derived MLB rows/imports from
 * `react_pages/FeeSchedule.tsx` and the temporary MLB regression cases.
 */
export const MLB_PRE_GAME_FEE_MULTIPLIER = 0.5;
export const MLB_PRE_GAME_SERIES_TICKERS = [
    'KXMLBGAME',
    'KXMLBTOTAL',
    'KXMLBSPREAD',
    'KXMLBRFI',
    'KXMLBF5',
    'KXMLBF5TOTAL',
    'KXMLBTEAMTOTAL',
    'KXMLBF5SPREAD',
    'KXMLBF3',
    'KXMLBF7',
    'KXMLBEXTRAS',
    'KXMLBHR',
    'KXMLBKS',
    'KXMLBHIT',
    'KXMLBHRR',
    'KXMLBTB',
    'KXMLBOUTS',
    'KXMLBRBI',
    'KXMLBSB',
] as const;

const MLB_PRE_GAME_SERIES_TICKER_SET = new Set<string>(MLB_PRE_GAME_SERIES_TICKERS);

// Keep this series-level fallback below event overrides so live-event pricing can
// replace the temporary pre-game multiplier until the Aug 20 rollback.
export const resolveSeriesFeeMultiplier = (
    seriesTicker: string | undefined,
    feeMultiplier: number | undefined,
): number | undefined =>
    seriesTicker && MLB_PRE_GAME_SERIES_TICKER_SET.has(seriesTicker) ? MLB_PRE_GAME_FEE_MULTIPLIER : feeMultiplier;

export type EffectiveFeeParams = Partial<Pick<SeriesVo, 'fee_type' | 'fee_multiplier'>>;
type SeriesFeeDefaults = EffectiveFeeParams & Partial<Pick<SeriesVo, 'ticker'>>;

/**
 * Effective fee params for an event: event-level fee overrides take precedence
 * over the series defaults. Single source of truth for that precedence rule.
 */
export const resolveEffectiveFeeParams = ({
    series,
    overrides,
}: {
    series?: SeriesFeeDefaults | null;
    overrides?: Pick<EventVo, 'fee_type_override' | 'fee_multiplier_override'> | null;
}): EffectiveFeeParams => ({
    fee_type: overrides?.fee_type_override ?? series?.fee_type,
    fee_multiplier:
        overrides?.fee_multiplier_override ?? resolveSeriesFeeMultiplier(series?.ticker, series?.fee_multiplier),
});

/**
 * Whether a market's fee waiver has lapsed. Missing or unparseable expirations
 * count as expired, so fees are charged rather than waived indefinitely.
 */
export const isFeeWaiverExpired = (feeWaiverExpiration: string | null | undefined, now = new Date()): boolean => {
    if (!feeWaiverExpiration) {
        return true;
    }
    const expirationMs = Date.parse(feeWaiverExpiration);
    if (Number.isNaN(expirationMs)) {
        return true;
    }
    return expirationMs < now.getTime();
};

/**
 * Applies a quadratic transformation to a price in cents.
 *
 * Formula: p(100 - p) / 100
 *
 * @example
 * applyQuadraticCents(1)  // => 0.99
 * applyQuadraticCents(50) // => 25
 *
 * @param {Decimal} priceInCents - The input price in cents
 */
const applyQuadraticCents = (priceInCents: Decimal) => {
    return priceInCents.times(new Decimal(100).minus(priceInCents)).dividedBy(100);
};

export const calculateTakerFeesDecimal = (
    contracts: Decimal,
    priceInCents: Decimal,
    seriesFeeMultiplier: Decimal = new Decimal(1),
    seriesFeeType: FeeType = 'quadratic',
): Decimal => {
    const feeTypesWithTakerFees = ['quadratic', 'quadratic_with_maker_fees'];
    const hasTakerFees = feeTypesWithTakerFees.includes(seriesFeeType);
    if (!hasTakerFees || contracts.lte(0)) return new Decimal(0);

    return TAKER_FEE.times(applyQuadraticCents(priceInCents)).times(contracts).times(seriesFeeMultiplier);
};

/**
 * Calculates expected fees for a given order (neither the max amount nor the exact amount)
 * @param  {number} contracts  # of contracts in the order
 * @param  {Decimal} priceInCents  Price in cents of order
 * @returns {number} Total fees in cents
 */
export const calculateTakerFees = (
    contracts: Decimal,
    priceInCents: Decimal,
    seriesFeeMultiplier: Decimal = new Decimal(1),
    seriesFeeType?: FeeType,
): number => {
    return calculateTakerFeesDecimal(
        new Decimal(contracts),
        new Decimal(priceInCents),
        new Decimal(seriesFeeMultiplier),
        seriesFeeType,
    )
        .ceil()
        .toNumber();
};

export const calculateTakersFeesUnrounded = (
    contracts: Decimal,
    priceInCents: Decimal,
    seriesFeeMultiplier: Decimal = new Decimal(1),
    seriesFeeType?: FeeType,
): number => {
    return calculateTakerFeesDecimal(
        new Decimal(contracts),
        new Decimal(priceInCents),
        new Decimal(seriesFeeMultiplier),
        seriesFeeType,
    ).toNumber();
};

export const calculateTakerFeeComponentForPrice = (
    priceCents: Decimal,
    seriesFeeMultiplier: Decimal = new Decimal(1),
    seriesFeeType?: FeeType,
): Decimal => {
    return calculateTakerFeesDecimal(new Decimal(1), priceCents, seriesFeeMultiplier, seriesFeeType);
};

export const calculateMakerFeesDecimal = (
    contracts: Decimal,
    priceInCents: Decimal,
    seriesFeeMultiplier: Decimal = new Decimal(1),
    seriesFeeType: FeeType = 'quadratic',
): Decimal => {
    const feeTypesWithMakerFees = ['quadratic_with_maker_fees'];
    const hasMakerFees = feeTypesWithMakerFees.includes(seriesFeeType);
    if (!hasMakerFees || contracts.lte(0)) return new Decimal(0);

    return MAKER_FEE.times(applyQuadraticCents(priceInCents)).times(contracts).times(seriesFeeMultiplier).ceil();
};

export const calculateMakerFees = (
    contracts: Decimal,
    priceInCents: Decimal,
    seriesFeeMultiplier = 1,
    seriesFeeType: FeeType = 'quadratic',
): number => {
    return calculateMakerFeesDecimal(
        new Decimal(contracts),
        new Decimal(priceInCents),
        new Decimal(seriesFeeMultiplier),
        seriesFeeType,
    )
        .ceil()
        .toNumber();
};

/**
 * Converts a price in cents to American odds format, inclusive of fees for the intended side (maker/taker).
 * Single source of truth used by both orderbook UI and BinaryMarket.americanOdds.
 *
 * @param priceInCents - The price in cents (0-100)
 * @param seriesFeeMultiplier - Optional fee multiplier from series
 * @param seriesFeeType - Optional fee type from series
 * @param feeSide - Whether to include maker vs taker fees for the intended action
 * @returns American odds string (e.g., "+150", "-200") or "--" if invalid
 */
export const priceToAmericanOdds = (
    priceInCents: number,
    seriesFeeMultiplier?: number,
    seriesFeeType?: FeeType,
    feeSide: 'maker' | 'taker' = 'taker',
    precision: 0 | 1 = 1,
): string => {
    const priceDecimal = new Decimal(priceInCents);

    // Look at 100 contracts to dilute impact of rounding (matches historical implementation)
    const seriesMultiplierDecimal = seriesFeeMultiplier ? new Decimal(seriesFeeMultiplier) : undefined;
    const fees =
        feeSide === 'maker'
            ? calculateMakerFeesDecimal(new Decimal(100), priceDecimal, seriesMultiplierDecimal, seriesFeeType)
            : calculateTakerFeesDecimal(new Decimal(100), priceDecimal, seriesMultiplierDecimal, seriesFeeType).ceil();

    const priceWithFees = priceDecimal.times(100).plus(fees).dividedBy(100);

    if (priceWithFees.gte(100) || priceWithFees.lte(0)) {
        return '--';
    }

    if (priceWithFees.gt(50)) {
        const raw = new Decimal(100).dividedBy(new Decimal(100).minus(priceWithFees)).times(100).minus(100).abs();
        const rounded = raw.toDecimalPlaces(precision, Decimal.ROUND_CEIL);
        return `-${rounded.mod(1).isZero() ? rounded.toFixed(0) : rounded.toFixed(precision)}`;
    } else {
        const raw = new Decimal(100).minus(priceWithFees).dividedBy(priceWithFees).times(100).abs();
        const rounded = raw.toDecimalPlaces(precision, Decimal.ROUND_FLOOR);
        return `+${rounded.mod(1).isZero() ? rounded.toFixed(0) : rounded.toFixed(precision)}`;
    }
};
